import { v4 as uuid } from 'uuid';
import saSdk from '../../../services/service-awards-sdk';

/*******************************************************************************
 * Initial state
 */
const initialState = {
    program: {},
    programLoading: false,
    programLoadingError: null,
    awardFile: {},
    awardFileLoading: false,
    awardFileLoadingError: null,
    awardFileImporting: false,
    awardFileImportingError: null,
    awardFileRecords: [],
    awardFileRecordsLoading: false,
    awardFileRecordsLoadingError: null,
    personFileStatus: {},
    // Non-preview award file submissions are now processed asynchronously by the
    // backend (service-awards #576): POST /award-files returns 202 immediately with
    // { awardFileId, loadStatus: 'pending' } and processing continues in the
    // background. awardFileLoadStatus mirrors awardFile.loadStatus for convenience,
    // and awardFileConflict captures the distinct 409 "already processing" case.
    awardFileLoadStatus: null,
    awardFileConflict: null
};

/*******************************************************************************
 * Non-async action types
 */
const SET_STATE = 'awardFile/setState';

/*******************************************************************************
 * Reducer
 */
export default function awardFileReducer( state = initialState, action ) {
    switch ( action.type ) {
        case SET_STATE: {
            return {
                ...state,
                ...action.state
            };
        }

        default:
            return state;
    }
}

/*******************************************************************************
 * Action creators
 */
function setState( state ) {
    return {
        type: SET_STATE,
        state
    };
}


function loadProgram( programId ) {
    return ( dispatch ) => {
        if ( programId ) {
            dispatch( setState( {
                programLoading: true,
                programLoadingError: null
            } ) );

            saSdk.getProgram( programId )
                .then( ( program ) => {
                    dispatch( setState( {
                        program,
                        programLoading: false
                    } ) );
                } )
                .catch( ( err ) => {
                    dispatch( setState( {
                        programLoading: false,
                        programLoadingError: err
                    } ) );
                } );
        }
    };
}

function unloadProgram( programId ) {
    return ( dispatch ) => {
        dispatch( setState( {
            program: {},
            programLoading: false,
            programLoadingErrors: null
        } ) );
    };
}


function modifyAwardFile( awardFile ) {
    return ( dispatch ) => {
        dispatch( setState( {
            awardFile,
            awardFileSavingErrors: null,
        } ) );
    };
}

function loadAwardFile( awardFileId ) {
    return ( dispatch ) => {
        if ( awardFileId ) {
            dispatch( setState( {
                awardFileLoading: true,
                awardFileLoadingError: null
            } ) );

            saSdk.getAwardFile( awardFileId )
                .then( ( awardFile ) => {
                    dispatch( setState( {
                        awardFile,
                        awardFileLoading: false
                    } ) );
                    return awardFile;
                } )
                .catch( ( err ) => {
                    dispatch( setState( {
                        awardFileLoading: false,
                        awardFileLoadingError: err
                    } ) );
                } );
        }
    };
}

function unloadAwardFile( awardFileId ) {
    return ( dispatch ) => {
        dispatch( setState( {
            awardFile: {},
            awardFileLoading: false,
            awardFileLoadingErrors: null
        } ) );
    };
}

function importAwardFile( programId, awardFileName, awardFileType, records, preview, personFileId ) {
let awardFileId;
    return ( dispatch ) => {
        dispatch( setState( {
            awardFileImporting: true,
            awardFileImportingError: null,
            awardFileConflict: null
        } ) );
        //Create id when it is a new file
        if ( preview == true ) {
            awardFileId = uuid();
        }
        const data = { programId, awardFileName, awardFileType, records, preview, personFileId };

        return saSdk.postAwardFile( data )
            .then( ( result ) => {
                if ( preview ) {
                    // Preview submissions are still handled synchronously by the backend
                    // and return the full processed result - unchanged from before.
                    dispatch( setState( {
                        awardFileImporting: false,
                        awardFile: {
                            awardFileId: result.awardFileId,
                            awardFileName: result.awardFileName,
                            createdAt: result.createdAt,
                            recordCount: result.recordCount,
                            recordErrors: result.recordErrors,
                            recordWarnings: result.recordWarnings,
                        },
                        awardFileRecords: result.records,
                        personFileStatus: result.currentStatus || {},
                    } ) );
                } else {
                    // Real (non-preview) submissions now return 202 immediately with
                    // { success, awardFileId, loadStatus: 'pending' } - the file is
                    // processed asynchronously on the backend. The full award file
                    // details (records, counts, etc) are only available later via
                    // loadAwardFile/loadAwardFileRecords once loadStatus reaches a
                    // terminal state ('completed' or 'failed').
                    dispatch( setState( {
                        awardFileImporting: false,
                        awardFileLoadStatus: result.loadStatus,
                        awardFile: {
                            awardFileId: result.awardFileId,
                            loadStatus: result.loadStatus
                        }
                    } ) );
                }

                return { awardFileId: result.awardFileId || awardFileId };
            } )
            .catch( ( err ) => {
                if ( err.status === 409 ) {
                    // The backend returns 409 when a file for this program/content is
                    // already mid-processing. The response body carries the in-flight
                    // awardFileId as a structured field on the first error entry
                    // (errors[0].awardFileId); fall back to parsing it out of the
                    // message text if that field isn't present (e.g. against an older
                    // backend deploy that hasn't rolled out the structured field yet).
                    const structuredAwardFileId = err.body && Array.isArray( err.body.errors ) &&
                        err.body.errors[ 0 ] && err.body.errors[ 0 ].awardFileId;
                    const messageAwardFileIdMatch = ( err.message || '' ).match( /awardFileId:\s*([a-zA-Z0-9-]+)/ );
                    const conflictAwardFileId = structuredAwardFileId ||
                        ( messageAwardFileIdMatch && messageAwardFileIdMatch[ 1 ] ) ||
                        null;

                    dispatch( setState( {
                        awardFileImporting: false,
                        awardFileConflict: {
                            awardFileId: conflictAwardFileId,
                            message: err.message
                        }
                    } ) );

                    return null;
                }

                dispatch( setState( {
                    awardFileImporting: false,
                    awardFileImportingError: err
                } ) );

                return null;
            } );
    };
}

function loadAwardFileRecords( awardFileId ) {
    return ( dispatch ) => {
        if ( awardFileId ) {
            dispatch( setState( {
                awardFileRecordsLoading: true,
                awardFileRecordsLoadingError: null
            } ) );

            saSdk.getAwardFileRecords( awardFileId )
                .then( ( awardFileRecords ) => {
                    dispatch( setState( {
                        awardFileRecords: awardFileRecords,
                        awardFileRecordsLoading: false
                    } ) );
                } )
                .catch( ( err ) => {
                    dispatch( setState( {
                        awardFileRecordsLoading: false,
                        awardFileRecordsLoadingError: err
                    } ) );
                } );
        }
    };
}
function getAwardFileErrors( awardFileId ) {
    return ( dispatch ) => {
        if ( awardFileId ) {
           return saSdk.getAwardFileErrors( awardFileId );
        }
    };
}
function unloadAwardFileRecords( awardFileId ) {
    return ( dispatch ) => {
        dispatch( setState( {
            awardFileRecords: [],
            awardFileRecordsLoading: false,
            awardFileRecordsLoadingErrors: null
        } ) );
    };
}

function getAwardFilePersonFile( awardFileId ) {
    return ( dispatch ) => {
        if ( awardFileId ) {
           return saSdk.getAwardFilePersonFile( awardFileId );
        }
    };
}

export function reEmitEvent( awardFileId ) {
    return ( dispatch ) => {
        return saSdk.reEmitEvent( { awardFileId } ).then( response => {
            if ( response.errors.length > 0 ) {
                console.error( response.errors );
                throw response;
            } else {
                return response;
            }
        } ).catch( ( err ) => {
            throw err;
        } );
    };
}

export const actions = {
    loadProgram,
    unloadProgram,
    loadAwardFile,
    unloadAwardFile,
    modifyAwardFile,
    importAwardFile,
    loadAwardFileRecords,
    unloadAwardFileRecords,
    getAwardFilePersonFile,
    getAwardFileErrors,
    reEmitEvent
};
